import { useState } from 'react';
import { 
  Book, ChevronDown, ChevronRight, Calendar, Layers, Handshake, 
  Tag, Building2, Users, Mail, Settings, Image, BarChart3,
  Plus, Edit, Trash2, Upload, Download, Search, Filter
} from 'lucide-react';

/**
 * UserManual - Interactive user manual for the admin panel
 */
export const UserManual = () => {
  const [expandedSections, setExpandedSections] = useState(['intro']);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const sections = [
    {
      id: 'intro',
      title: 'Introducción',
      icon: Book,
      content: (
        <div className="space-y-4">
          <p>
            Bienvenido al panel de administración de <strong>Event Cinema</strong>. Desde aquí podrás 
            gestionar todos los aspectos de tu plataforma de eventos culturales.
          </p>
          <div className="bg-[var(--primary)]/10 border border-[var(--primary)]/30 p-4">
            <p className="text-sm">
              <strong>Consejo:</strong> Usa las pestañas de la parte superior para navegar entre 
              las diferentes secciones del panel de administración.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'events',
      title: 'Gestión de Eventos',
      icon: Calendar,
      content: (
        <div className="space-y-4">
          <h4 className="text-white font-medium">Crear un nuevo evento</h4>
          <ol className="list-decimal list-inside space-y-2 text-white/70">
            <li>Haz clic en el botón <span className="text-[var(--primary)]">"+ Nuevo Evento"</span></li>
            <li>Completa los campos obligatorios:
              <ul className="list-disc list-inside ml-4 mt-1 text-sm">
                <li><strong>Título:</strong> Nombre del evento (ej: "La Traviata")</li>
                <li><strong>Fecha:</strong> Fecha de la retransmisión</li>
                <li><strong>Hora:</strong> Hora de inicio (solo para eventos en directo)</li>
                <li><strong>Género:</strong> Categoría del evento</li>
                <li><strong>Teatro/Venue:</strong> Lugar de origen</li>
                <li><strong>Duración:</strong> En minutos</li>
              </ul>
            </li>
            <li>Añade una imagen de póster (URL)</li>
            <li>Escribe la descripción del evento</li>
            <li>Configura las opciones de venta SMART si aplica</li>
            <li>Haz clic en <span className="text-[var(--primary)]">"Guardar"</span></li>
          </ol>
          
          <h4 className="text-white font-medium mt-6">Añadir cines a un evento</h4>
          <ol className="list-decimal list-inside space-y-2 text-white/70">
            <li>En la sección "Cines que proyectan este evento", haz clic en <span className="text-[var(--primary)]">"+ Añadir Cine"</span></li>
            <li>Selecciona el cine de la lista o añade uno nuevo</li>
            <li>Configura:
              <ul className="list-disc list-inside ml-4 mt-1 text-sm">
                <li><strong>Horario:</strong> Hora de proyección en ese cine</li>
                <li><strong>Venta SMART:</strong> Activa si tiene descuento del 25%</li>
                <li><strong>Código embed:</strong> Para la compra de entradas SMART</li>
              </ul>
            </li>
          </ol>
          
          <h4 className="text-white font-medium mt-6">Importar cines desde Excel</h4>
          <div className="bg-white/5 p-4 rounded">
            <p className="text-white/70 text-sm mb-2">
              Puedes importar múltiples cines a la vez usando un archivo Excel (.xlsx) con las siguientes columnas:
            </p>
            <code className="text-xs text-[var(--primary)] block bg-black/30 p-2">
              cinema_name | city | comunidad_autonoma | schedule | has_smart | embed_code | website_url
            </code>
          </div>
        </div>
      )
    },
    {
      id: 'hero',
      title: 'Hero/Slides',
      icon: Layers,
      content: (
        <div className="space-y-4">
          <p className="text-white/70">
            Los slides del hero aparecen en la página principal como carrusel destacado.
          </p>
          
          <h4 className="text-white font-medium">Crear un slide</h4>
          <ol className="list-decimal list-inside space-y-2 text-white/70">
            <li>Haz clic en <span className="text-[var(--primary)]">"+ Nuevo Slide"</span></li>
            <li>Completa los campos:
              <ul className="list-disc list-inside ml-4 mt-1 text-sm">
                <li><strong>Título:</strong> Texto principal del slide</li>
                <li><strong>Subtítulo:</strong> Texto secundario</li>
                <li><strong>URL de imagen:</strong> Imagen de fondo (recomendado 1920x800px)</li>
                <li><strong>Enlace:</strong> URL al hacer clic (opcional)</li>
              </ul>
            </li>
            <li>Guarda el slide</li>
          </ol>
          
          <div className="bg-[var(--primary)]/10 border border-[var(--primary)]/30 p-4 mt-4">
            <p className="text-sm">
              <strong>Tip:</strong> Arrastra y suelta los slides para reordenarlos. El primer slide 
              será el que aparezca inicialmente.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'partners',
      title: 'Partners/Colaboradores',
      icon: Handshake,
      content: (
        <div className="space-y-4">
          <p className="text-white/70">
            Los logos de partners aparecen en la sección "Nuestros Partners" de la página principal.
          </p>
          
          <h4 className="text-white font-medium">Añadir un partner</h4>
          <ol className="list-decimal list-inside space-y-2 text-white/70">
            <li>Haz clic en <span className="text-[var(--primary)]">"+ Nuevo Partner"</span></li>
            <li>Completa los campos:
              <ul className="list-disc list-inside ml-4 mt-1 text-sm">
                <li><strong>Nombre:</strong> (Opcional) Nombre del partner</li>
                <li><strong>URL del logo:</strong> (Obligatorio) Enlace a la imagen del logo</li>
                <li><strong>Web:</strong> (Opcional) Enlace al sitio web del partner</li>
              </ul>
            </li>
            <li>Guarda el partner</li>
          </ol>
          
          <div className="bg-white/5 p-4 mt-4">
            <p className="text-sm text-white/50">
              <strong>Formato recomendado:</strong> PNG con fondo transparente, tamaño aproximado 200x100px
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'genres',
      title: 'Géneros',
      icon: Tag,
      content: (
        <div className="space-y-4">
          <p className="text-white/70">
            Los géneros permiten categorizar los eventos (Ópera, Ballet, Concierto, Teatro, etc.).
          </p>
          
          <h4 className="text-white font-medium">Gestionar géneros</h4>
          <ul className="list-disc list-inside space-y-2 text-white/70">
            <li>Para <strong>añadir</strong>: Escribe el nombre y haz clic en "+ Añadir"</li>
            <li>Para <strong>editar</strong>: Haz clic en el icono de lápiz junto al género</li>
            <li>Para <strong>eliminar</strong>: Haz clic en el icono de papelera (solo si no hay eventos asociados)</li>
          </ul>
          
          <div className="bg-red-500/10 border border-red-500/30 p-4 mt-4">
            <p className="text-sm text-red-400">
              <strong>Advertencia:</strong> No elimines géneros que tengan eventos asociados, 
              ya que esos eventos dejarán de mostrarse correctamente.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'cities',
      title: 'Ciudades',
      icon: Building2,
      content: (
        <div className="space-y-4">
          <p className="text-white/70">
            Las ciudades se usan para filtrar eventos y agrupar cines por ubicación.
          </p>
          
          <h4 className="text-white font-medium">Añadir una ciudad</h4>
          <ol className="list-decimal list-inside space-y-2 text-white/70">
            <li>Escribe el nombre de la ciudad en el campo de texto</li>
            <li>Haz clic en <span className="text-[var(--primary)]">"+ Añadir"</span></li>
          </ol>
          
          <p className="text-white/50 text-sm mt-4">
            Las ciudades también se crean automáticamente cuando añades cines con nuevas ubicaciones.
          </p>
        </div>
      )
    },
    {
      id: 'members',
      title: 'myEventCinema (Socios)',
      icon: Users,
      content: (
        <div className="space-y-4">
          <p className="text-white/70">
            Gestiona los usuarios registrados en el programa de fidelización myEventCinema.
          </p>
          
          <h4 className="text-white font-medium">Información de socios</h4>
          <p className="text-white/70">Cada socio tiene:</p>
          <ul className="list-disc list-inside space-y-1 text-white/70 text-sm">
            <li><strong>Puntos:</strong> Acumulados por compras y actividad</li>
            <li><strong>Nivel:</strong> Bronce (0-499), Plata (500-999), Oro (1000+)</li>
            <li><strong>Favoritos:</strong> Eventos guardados</li>
            <li><strong>Estado de comunicaciones:</strong> Si acepta recibir newsletters</li>
          </ul>
          
          <h4 className="text-white font-medium mt-6">Sincronización con ActiveCampaign</h4>
          <p className="text-white/70 text-sm">
            Los socios se sincronizan automáticamente con ActiveCampaign cuando se registran. 
            Puedes forzar una sincronización manual desde el botón "Sincronizar con AC".
          </p>
        </div>
      )
    },
    {
      id: 'newsletter',
      title: 'Newsletter',
      icon: Mail,
      content: (
        <div className="space-y-4">
          <p className="text-white/70">
            Gestiona los suscriptores del newsletter y visualiza estadísticas.
          </p>
          
          <h4 className="text-white font-medium">Estadísticas disponibles</h4>
          <ul className="list-disc list-inside space-y-1 text-white/70 text-sm">
            <li>Total de suscriptores activos</li>
            <li>Nuevos suscriptores (última semana)</li>
            <li>Bajas recientes</li>
          </ul>
          
          <h4 className="text-white font-medium mt-6">Exportar lista</h4>
          <p className="text-white/70 text-sm">
            Usa el botón "Exportar CSV" para descargar la lista completa de emails 
            para importar en tu plataforma de email marketing.
          </p>
        </div>
      )
    },
    {
      id: 'config',
      title: 'Configuración',
      icon: Settings,
      content: (
        <div className="space-y-4">
          <p className="text-white/70">
            Personaliza la apariencia y configuración general del sitio.
          </p>
          
          <h4 className="text-white font-medium">Opciones disponibles</h4>
          <ul className="list-disc list-inside space-y-2 text-white/70">
            <li><strong>Logo del header:</strong> Logo que aparece en la barra de navegación</li>
            <li><strong>Logo del footer:</strong> Logo que aparece al pie de página</li>
            <li><strong>Color primario:</strong> Color de acento de la web (dorado por defecto)</li>
            <li><strong>Textos del footer:</strong> Información de contacto y copyright</li>
          </ul>
          
          <div className="bg-[var(--primary)]/10 border border-[var(--primary)]/30 p-4 mt-4">
            <p className="text-sm">
              <strong>Formato de logos:</strong> Se recomienda PNG con fondo transparente. 
              El logo del header debe tener altura máxima de 50px.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'gallery',
      title: 'Galería',
      icon: Image,
      content: (
        <div className="space-y-4">
          <p className="text-white/70">
            Gestiona las imágenes disponibles para usar en eventos y slides.
          </p>
          
          <h4 className="text-white font-medium">Subir imágenes</h4>
          <ol className="list-decimal list-inside space-y-2 text-white/70">
            <li>Haz clic en <span className="text-[var(--primary)]">"+ Subir Imagen"</span></li>
            <li>Selecciona la imagen desde tu ordenador</li>
            <li>La imagen se subirá y obtendrás una URL para usar en eventos</li>
          </ol>
          
          <h4 className="text-white font-medium mt-6">Copiar URL de imagen</h4>
          <p className="text-white/70 text-sm">
            Haz clic en cualquier imagen de la galería para copiar su URL al portapapeles.
            Luego pega esa URL en el campo correspondiente al crear un evento o slide.
          </p>
        </div>
      )
    },
    {
      id: 'analytics',
      title: 'Analíticas',
      icon: BarChart3,
      content: (
        <div className="space-y-4">
          <p className="text-white/70">
            El dashboard de analíticas te permite monitorizar el tráfico y comportamiento de los usuarios.
          </p>
          
          <h4 className="text-white font-medium">Métricas en tiempo real</h4>
          <ul className="list-disc list-inside space-y-1 text-white/70 text-sm">
            <li><strong>Usuarios activos:</strong> Visitantes en los últimos 5 minutos</li>
            <li><strong>Vistas en tiempo real:</strong> Páginas vistas ahora mismo</li>
            <li><strong>Páginas activas:</strong> Qué páginas están viendo los usuarios</li>
          </ul>
          
          <h4 className="text-white font-medium mt-6">Métricas históricas</h4>
          <ul className="list-disc list-inside space-y-1 text-white/70 text-sm">
            <li><strong>Visitas totales:</strong> Páginas vistas en el período seleccionado</li>
            <li><strong>Sesiones únicas:</strong> Número de visitantes únicos</li>
            <li><strong>Páginas por sesión:</strong> Promedio de páginas vistas por visita</li>
            <li><strong>Dispositivos:</strong> Desglose por desktop, móvil y tablet</li>
            <li><strong>Contenidos más vistos:</strong> Eventos con más tráfico</li>
          </ul>
        </div>
      )
    }
  ];

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <Book size={32} className="text-[var(--primary)]" />
          <h2 className="font-['Playfair_Display'] text-3xl text-white">Manual de Usuario</h2>
        </div>
        <p className="text-white/60">
          Guía completa para gestionar todos los aspectos de Event Cinema desde el panel de administración.
        </p>
      </div>

      {/* Sections */}
      <div className="space-y-2">
        {sections.map((section) => {
          const Icon = section.icon;
          const isExpanded = expandedSections.includes(section.id);
          
          return (
            <div key={section.id} className="border border-white/10 overflow-hidden">
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full flex items-center justify-between p-4 bg-white/5 hover:bg-white/10 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Icon size={20} className="text-[var(--primary)]" />
                  <span className="text-white font-medium">{section.title}</span>
                </div>
                {isExpanded ? (
                  <ChevronDown size={20} className="text-white/50" />
                ) : (
                  <ChevronRight size={20} className="text-white/50" />
                )}
              </button>
              
              {isExpanded && (
                <div className="p-6 bg-[#0a0a0a] border-t border-white/5">
                  {section.content}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Quick Actions Reference */}
      <div className="mt-8 p-6 bg-[var(--primary)]/10 border border-[var(--primary)]/30">
        <h3 className="text-white font-medium mb-4 flex items-center gap-2">
          <Settings size={18} className="text-[var(--primary)]" />
          Referencia Rápida de Acciones
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div className="flex items-center gap-2 text-white/70">
            <Plus size={14} className="text-green-400" />
            <span>Añadir nuevo</span>
          </div>
          <div className="flex items-center gap-2 text-white/70">
            <Edit size={14} className="text-blue-400" />
            <span>Editar</span>
          </div>
          <div className="flex items-center gap-2 text-white/70">
            <Trash2 size={14} className="text-red-400" />
            <span>Eliminar</span>
          </div>
          <div className="flex items-center gap-2 text-white/70">
            <Upload size={14} className="text-purple-400" />
            <span>Subir archivo</span>
          </div>
          <div className="flex items-center gap-2 text-white/70">
            <Download size={14} className="text-cyan-400" />
            <span>Descargar/Exportar</span>
          </div>
          <div className="flex items-center gap-2 text-white/70">
            <Search size={14} className="text-yellow-400" />
            <span>Buscar</span>
          </div>
          <div className="flex items-center gap-2 text-white/70">
            <Filter size={14} className="text-orange-400" />
            <span>Filtrar</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManual;
